import { PropsWithChildren, ReactNode, useEffect } from 'react'
import { Box, Container, Typography } from '@mui/material'
import { Link } from 'react-router-dom'

import { StandardPageHeader } from 'src/components/MUIBased/pageHeaders'
import { NavBarHeading } from 'src/components/MUIBased/NavBarHeading'
import { ChevronRight } from 'src/components/MUIBased/icons'
import { HorizontalDivider } from '../../components/MUIBased/dividers'

import { useAppointmentSuggestions } from 'src/hooks/useAppointmentSuggestions'
import {
    useHasCarePlan,
    useGetUnseenCarePlanInterventions
} from 'src/hooks/useCarePlan'
import { useAmpli } from 'src/hooks/useAmpli'
import { useAmpliFeatureFlag } from 'src/AmplitudeExperimentProvider'

const YourCareItem = (
    props: PropsWithChildren<{ title: ReactNode; url: string }>
) => {
    const { title, url } = props
    return (
        <Box
            sx={{
                mb: '16px',
                border: 0
            }}
        >
            <Link to={url}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {props.children}
                    <Box
                        sx={{
                            pr: 4,
                            pl: 2,
                            flex: 1
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                mb: 0,
                                fontWeight: 'bold'
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>
                    <ChevronRight color={'#000000'} />
                </Box>
            </Link>
        </Box>
    )
}

export type ActionItem = {
    card: ReactNode
    item: string
}

export const YourCare = () => {
    const hasCarePlan = useHasCarePlan()
    const { appointmentSuggestions } = useAppointmentSuggestions({
        status: 'pending',
        actionDismissed: false
    })
    const interventions = useGetUnseenCarePlanInterventions()
    const hasUnseenCarePlan = interventions.length > 0
    const { yourCareViewed } = useAmpli()

    useEffect(() => {
        yourCareViewed()
    }, [yourCareViewed])

    return (
        <>
            <StandardPageHeader showAppointmentBookingButton={true} />
            <NavBarHeading href="/mui">Your Care</NavBarHeading>
            <Container>
                {(hasCarePlan ||
                    (appointmentSuggestions &&
                        appointmentSuggestions.length > 0)) && (
                    <>
                        <YourCareItem
                            title={
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Box>Your Care Plan</Box>
                                    {hasUnseenCarePlan && <Box>NEW</Box>}
                                </Box>
                            }
                            url={`/your-care/plan`}
                        />
                        <HorizontalDivider />
                    </>
                )}
                <YourCareItem
                    title="Your Health Record"
                    url="/mui/your-care/health-record"
                />
                <HorizontalDivider />
                <YourCareItem
                    title={'Appointment History'}
                    url="/mui/appointment-history-list"
                />
                <HorizontalDivider />
                <YourCareItem
                    title="Lab Results"
                    url="/mui/your-care/lab-results"
                />
                <HorizontalDivider />
                <YourCareItem
                    title={'Appointment Billing'}
                    url="/mui/appointment-billing-list"
                />
                <HorizontalDivider />
                <YourCareItem
                    title="Account Details"
                    url="/mui/account-details"
                />
                <HorizontalDivider />
            </Container>
        </>
    )
}

export default YourCare
